import React from 'react';
import { CustomerDTO, InvoiceDTO, formatMoney } from '../domain/models';
import { CalculatorService } from '../services/CalculatorService';

export type InvoicePreviewProps = Pick<InvoiceDTO, 'items'> & {
  customer: CustomerDTO;
  invoiceNumber?: string;
  issueDate?: string;
  sellerName?: string;
  sellerAddress?: string;
};

const calc = new CalculatorService();

export function InvoicePreview({ customer, invoiceNumber, issueDate, items, sellerName = 'BillingCalc GmbH', sellerAddress = 'Musterstraße 1, 10115 Berlin' }: InvoicePreviewProps) {
  const totals = calc.totals(items);
  const formattedIssueDate = (() => {
    if (!issueDate) return '-';
    // Erwartet ISO (YYYY-MM-DD). Fallback: zeige Original, wenn Date() fehlschlägt.
    const d = new Date(issueDate);
    if (isNaN(d.getTime())) return issueDate; // Unverändert anzeigen, falls kein valides Datum
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  })();

  return (
    <div id="invoice-print" className="bg-white rounded-lg shadow p-4 mt-6 print:shadow-none">
      <h2 className="font-semibold mb-3">Rechnungsvorschau</h2>

      <div className="border rounded">
        {/* A4-ähnliche Fläche */}
        <div className="p-6 md:p-10 space-y-6">
          {/* Briefkopf */}
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-semibold text-emerald-800">Entsorgung Kretzschmann</div>
            <div className="mt-1 text-emerald-900">Inhaber Justin Kretzschmann</div>
          </div>

          <div className="mt-6 border-t-2 border-emerald-900" />
          <div className="text-center font-semibold uppercase tracking-[.35em] text-emerald-900 py-2">
            Schrott - Demontagen - Autoverwertung
          </div>
          <div className="border-b-2 border-emerald-900" />

          {/* Anschriftenzeile */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
            <div>
              <div className="font-semibold">{[customer.firstname, customer.lastname].filter(Boolean).join(' ') || customer.mail || '—'}</div>
              <div className="text-sm">
                {[customer.street, customer.buildingNumber].filter(Boolean).join(' ') || null}<br />
                {[customer.postcode, customer.city].filter(Boolean).join(' ') || null}<br />
                {customer.country || null}
              </div>
            </div>
            <div className="text-right text-sm">
              <div className="font-semibold">Entsorgung Kretzschmann</div>
              <div>Beckhausstrasse 3-5</div>
              <div>33611 Bielefeld</div>
              <div className="mt-2">0521/ 60700</div>
              <div>0521/ 171788</div>
              <div className="mt-2">info@kretzschmann-entsorgung.de</div>
            </div>
          </div>

          {/* Rechnungsdaten */}
          <div className="mt-4 text-sm text-right space-y-0.5">
            <div><span className="text-slate-500">Rechnungsnummer:</span> <span className="font-medium">{invoiceNumber || '-'}</span></div>
            <div><span className="text-slate-500">Datum:</span> <span className="font-medium">{formattedIssueDate}</span></div>
            <div><span className="text-slate-500">Kundennummer:</span> <span className="font-medium">{customer?.customerNumber || '-'}</span></div>
            <div><span className="text-slate-500">Ausweis-Nr.:</span> <span className="font-medium">{customer?.idCardNr || '-'}</span></div>
          </div>

          {/* Positionstabelle */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
              <tr className="border-b text-slate-600">
                <th className="text-left py-2 pr-2">Beschreibung</th>
                <th className="text-right py-2 px-2">Menge</th>
                <th className="text-left py-2 px-2">Einheit</th>
                <th className="text-right py-2 px-2">Einzelpreis</th>
                <th className="text-right py-2 pl-2">Summe</th>
              </tr>
              </thead>
              <tbody>
              {items.length === 0 && (
                <tr>
                  <td className="py-3 text-slate-500" colSpan={5}>Keine Positionen</td>
                </tr>
              )}
              {items.map((it, idx) => {
                const qty = Number(it.quantity || 0);
                const price = Number(it.product.unitPrice || 0);
                const lineTotal = qty * price;
                return (
                  <tr key={idx} className="border-b last:border-0">
                    <td className="py-2 pr-2 align-top">
                      <div className="font-medium">{it.product.name || '—'}</div>
                      {it.product.productKey && (
                        <div className="text-xs text-slate-500">#{it.product.productKey}</div>
                      )}
                      {it.product.description && (
                        <div className="text-xs text-slate-500 mt-1">{it.product.description}</div>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right align-top">{qty}</td>
                    <td className="py-2 px-2 align-top">{it.product.unit || 'Stk.'}</td>
                    <td className="py-2 px-2 text-right align-top">{formatMoney(price)}</td>
                    <td className="py-2 pl-2 text-right align-top">{formatMoney(lineTotal)}</td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>

          {/* Summenbereich */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div />
            <div className="md:justify-self-end w-full md:w-96">
              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-base font-semibold"><span>Gesamtsumme</span><span>{formatMoney(totals.grandTotal)}</span></div>
              </div>
            </div>
          </div>

          {/* Fußzeile */}
          <div className="text-xs text-slate-500 pt-8 border-t">
            Barauszahlung Kasse - Leistungsdatum ist Lieferdatum
          </div>
        </div>
      </div>

      {/* Druck-Styles */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          /* Hide everything except the invoice preview card */
          body * { visibility: hidden !important; }
          #invoice-print, #invoice-print * { visibility: visible !important; }
          #invoice-print { position: absolute !important; left: 0; top: 0; width: 100%; box-shadow: none !important; background: #fff !important; }
          html, body { background: #fff !important; }
        }
      `}</style>
    </div>
  );
}
